import { CommonModule, DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TabViewModule } from 'primeng/tabview';
import { ToastModule } from 'primeng/toast';
import { from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  AllocatedTransaction,
  BankEnum,
  BankReconciliationService,
  BankReconciliationTransaction,
  ReconcileResult,
} from '../../service/bank-reconciliation.service';
import { IcecashReportsService } from '../../service/icecash-reports.service';
import { UserService } from '../../service/user.service';
import { bankOptions } from './banks.list';

@Component({
  selector: 'app-bank-reconciliation',
  standalone: true,
  imports: [CommonModule, FormsModule, TabViewModule, ToastModule, ButtonModule,
    DropdownModule, TableModule, ProgressBarModule, ProgressSpinnerModule, CalendarModule],
  templateUrl: './bank-reconciliation.component.html',
  styleUrl: './bank-reconciliation.component.scss',
  providers: [MessageService, DatePipe]
})
export class BankReconciliationComponent {

  private readonly service = inject(BankReconciliationService);
  private readonly icecashService = inject(IcecashReportsService);
  private readonly userService = inject(UserService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly datePipe = inject(DatePipe);
  messageService = inject(MessageService);

  private readonly maxFileSize = 10 * 1024 * 1024;

  banks = signal(bankOptions);

  // ── Zinara upload ──────────────────────────────────────────────────────────
  zinaraFile: File | null = null;
  isZinaraUploading = false;

  // ── Auto reconciliation ────────────────────────────────────────────────────
  dateFrom: Date | null = null;
  dateTo: Date | null = null;
  submittingAuto = signal(false);
  reconcileResults = signal<ReconcileResult[]>([]);

  // ── Icecash promo upload ───────────────────────────────────────────────────
  readonly currencies = [
    { label: 'ZWG', value: 1 },
    { label: 'USD', value: 2 },
  ];
  promoFile: File | null = null;
  promoCurrencyId = 1;
  isPromoUploading = false;

  // ── Manual allocation ──────────────────────────────────────────────────────
  users = signal<any[]>([]);
  transactions = signal<BankReconciliationTransaction[]>([]);
  loadingUsers = signal(false);
  loadingTransactions = signal(false);
  submittingManual = signal(false);

  selectedUser: number | null = null;
  selectedTransactions: BankReconciliationTransaction[] = [];

  filterBank: BankEnum | null = null;
  filterIsAllocated: boolean | null = null;

  submittingAutomatic = signal(false);
  automaticAllocatedResults = signal<AllocatedTransaction[]>([]);
  automaticUnallocatedResults = signal<BankReconciliationTransaction[]>([]);

  allocationStatusOptions: { label: string; value: boolean }[] = [
    { label: 'Unallocated', value: false },
    { label: 'Allocated', value: true },
  ];

  ngOnInit(): void {
    this.loadUsers();
  }

  private loadUsers(): void {
    this.loadingUsers.set(true);
    this.userService.listUsers({ pageNumber: 0, pageSize: 200 }).subscribe({
      next: (res) => {
        this.users.set(Array.isArray(res) ? res : (res.users ?? res.content ?? []));
        this.loadingUsers.set(false);
      },
      error: () => this.loadingUsers.set(false),
    });
  }

  loadTransactions(): void {
    if (this.filterBank === null || this.filterIsAllocated === null) return;
    this.loadingTransactions.set(true);
    this.selectedTransactions = [];
    this.service.findTransactions(this.filterBank, this.filterIsAllocated).subscribe({
      next: (res) => {
        this.transactions.set(res.bankReconciliationTransactions ?? []);
        this.loadingTransactions.set(false);
      },
      error: (err: any) => {
        this.loadingTransactions.set(false);
        const detail = typeof err.error === 'string' ? err.error : err.error?.message || JSON.stringify(err.error);
        this.messageService.add({ severity: 'error', summary: `Load Failed (${err.status})`, life: 10000, detail: detail || 'An error occurred while loading transactions.' });
      },
    });
  }

  get filtersValid(): boolean {
    return this.filterBank !== null && this.filterIsAllocated !== null;
  }

  runAutomaticAllocation(): void {
    if (!this.filterBank) return;
    this.submittingAutomatic.set(true);
    this.automaticAllocatedResults.set([]);
    this.automaticUnallocatedResults.set([]);
    this.service.runAutomatic(this.filterBank).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (res) => {
        this.submittingAutomatic.set(false);
        this.automaticAllocatedResults.set(res?.allocatedBankReconciliationTransactions ?? []);
        this.automaticUnallocatedResults.set(res?.unAllocatedBankReconciliationTransactions ?? []);
        const count = res?.allocatedBankReconciliationTransactions?.length ?? 0;
        this.messageService.add({ severity: 'success', summary: 'Automatic Allocation', life: 3000, detail: res?.message ?? `Allocated ${count} transaction(s).` });
      },
      error: (err: any) => {
        this.submittingAutomatic.set(false);
        const detail = typeof err.error === 'string' ? err.error : err.error?.message || JSON.stringify(err.error);
        this.messageService.add({ severity: 'error', summary: `Automatic Allocation Failed (${err.status})`, life: 10000, detail: detail || 'An error occurred during automatic allocation.' });
      },
    });
  }

  submitManual(): void {
    if (!this.selectedUser || !this.selectedTransactions.length) return;
    this.submittingManual.set(true);

    const transactionIds = this.selectedTransactions.map(tx => tx.id);
    this.service.runManual(this.selectedUser, transactionIds).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (res) => {
        this.submittingManual.set(false);
        this.selectedTransactions = [];
        this.selectedUser = null;
        this.messageService.add({ severity: 'success', summary: 'Allocation Success', life: 3000, detail: res?.message ?? 'Transactions allocated successfully.' });
        this.loadTransactions();
      },
      error: (err: any) => {
        this.submittingManual.set(false);
        const detail = typeof err.error === 'string' ? err.error : err.error?.message || JSON.stringify(err.error);
        this.messageService.add({ severity: 'error', summary: `Allocation Failed (${err.status})`, life: 10000, detail: detail || 'An error occurred during allocation.' });
      },
    });
  }

  // ── Zinara ─────────────────────────────────────────────────────────────────

  onZinaraFileSelect(event: Event): void {
    const file = this.extractFile(event, 'zinaraInput');
    if (file) this.zinaraFile = file;
  }

  uploadZinaraFile(): void {
    if (!this.zinaraFile) return;
    this.isZinaraUploading = true;

    from(this.zinaraFile.arrayBuffer()).pipe(
      map(buffer => {
        const blob = new Blob([buffer], { type: this.zinaraFile!.type || 'application/octet-stream' });
        const formData = new FormData();
        formData.append('file', blob, this.zinaraFile!.name);
        return formData;
      }),
      switchMap(formData => this.service.uploadZinaraReport(formData)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (value) => {
        this.isZinaraUploading = false;
        this.zinaraFile = null;
        this.messageService.add({ severity: 'success', summary: 'Upload Success', life: 3000, detail: "Copied records : " + value['recordsCopied'] + " and records failed : " + value['recordsFailed'] });
      },
      error: (err: any) => {
        this.isZinaraUploading = false;
        const detail = typeof err.error === 'string' ? err.error : err.error?.message || JSON.stringify(err.error);
        this.messageService.add({ severity: 'error', summary: `Upload Failed (${err.status})`, life: 10000, detail: detail || 'An error occurred during upload.' });
      },
    });
  }

  clearZinaraFile(): void {
    this.zinaraFile = null;
  }

  // ── Auto reconciliation ────────────────────────────────────────────────────

  submitReconcile(): void {
    if (!this.dateFrom || !this.dateTo) return;
    this.submittingAuto.set(true);
    this.reconcileResults.set([]);

    const from_ = this.datePipe.transform(this.dateFrom, 'yyyy-MM-dd')!;
    const to_ = this.datePipe.transform(this.dateTo, 'yyyy-MM-dd')!;

    this.service.runReconcile(from_, to_).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (res) => {
        this.submittingAuto.set(false);
        this.reconcileResults.set(res ?? []);
        if (!res?.length) {
          this.messageService.add({ severity: 'info', summary: 'No Results', detail: 'No reconciliation data found for the selected period.' });
        }
      },
      error: (err: any) => {
        this.submittingAuto.set(false);
        this.messageService.add({ severity: 'error', summary: 'Reconciliation Failed', detail: err?.error?.message ?? 'An error occurred.' });
      },
    });
  }

  // ── Icecash promo upload ───────────────────────────────────────────────────

  onPromoFileSelect(event: Event): void {
    const file = this.extractFile(event, 'promoInput');
    if (file) this.promoFile = file;
  }

  uploadPromoFile(): void {
    if (!this.promoFile) return;
    this.isPromoUploading = true;

    from(this.promoFile.arrayBuffer()).pipe(
      map(buffer => {
        const blob = new Blob([buffer], { type: this.promoFile!.type || 'text/csv' });
        const formData = new FormData();
        formData.append('file', blob, this.promoFile!.name);
        formData.append('currencyId', String(this.promoCurrencyId));
        return formData;
      }),
      switchMap(formData => this.icecashService.uploadPromoFiles(formData)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (value) => {
        this.isPromoUploading = false;
        this.promoFile = null;
        this.messageService.add({
          severity: 'success', summary: 'Upload Success', life: 3000,
          detail: `File uploaded successfully. ${value['recordsCopied']} copied, ${value['recordsFailed']} failed.`,
        });
      },
      error: (error: any) => {
        this.isPromoUploading = false;
        const detail = typeof error.error === 'string' ? error.error : error.error?.message || JSON.stringify(error.error);
        this.messageService.add({ severity: 'error', summary: `Upload Failed (${error.status})`, life: 10000, detail: detail || 'An error occurred during upload.' });
      },
    });
  }

  clearPromoFile(): void {
    this.promoFile = null;
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  private extractFile(event: Event, _ref: string): File | null {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    if (!file) return null;

    if (file.size === 0) {
      this.messageService.add({ severity: 'error', summary: 'Empty File', detail: `${file.name} is empty.` });
      input.value = '';
      return null;
    }
    if (file.size > this.maxFileSize) {
      this.messageService.add({ severity: 'error', summary: 'File Too Large', detail: `${file.name} exceeds the 10MB limit.` });
      input.value = '';
      return null;
    }

    this.messageService.add({
      severity: 'success', summary: 'File Selected',
      detail: `${file.name} (${(file.size / 1024).toFixed(1)} KB) is ready for upload.`,
    });
    return file;
  }

}
